import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, MessageCircle, Mail, Navigation } from "lucide-react";
import { api, errMsg, telHref, waHref } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Seo, businessLd } from "@/components/site/Seo";
import { PageHero } from "@/components/site/Common";
import { SocialLinks, WorkingHours } from "@/components/site/Social";
import CallbackForm from "@/components/site/CallbackForm";
import { Field, Honeypot, ConsentBox, SubmitBtn, SuccessBox, isEmail, isPhone } from "@/components/site/FormBits";

const INIT = { name: "", phone: "", email: "", subject: "", message: "", kvkk_ack: false, website: "" };

function MapBox({ query }) {
  const [consent, setConsent] = useState(() => localStorage.getItem("cookie_consent") === "accepted");
  const [force, setForce] = useState(false);
  useEffect(() => {
    const h = () => setConsent(localStorage.getItem("cookie_consent") === "accepted");
    window.addEventListener("cookie-consent-changed", h);
    return () => window.removeEventListener("cookie-consent-changed", h);
  }, []);
  const q = encodeURIComponent(query || "");
  if (consent || force) {
    return <iframe title="Rüzgâr Özel Güvenlik konum haritası" src={`https://maps.google.com/maps?q=${q}&z=16&output=embed`} className="h-80 w-full rounded-lg border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="contact-map" />;
  }
  return (
    <div className="flex h-80 flex-col items-center justify-center rounded-lg border border-dashed border-zinc-300 bg-zinc-100 p-6 text-center" data-testid="contact-map-placeholder">
      <MapPin className="h-8 w-8 text-[color:var(--red)]" />
      <p className="mt-3 max-w-sm text-sm text-zinc-600">Harita Google Haritalar üzerinden yüklenir ve üçüncü taraf çerezleri kullanabilir.</p>
      <button onClick={() => setForce(true)} className="btn-outline mt-4" data-testid="contact-map-load-btn">Haritayı Göster</button>
    </div>
  );
}

function ContactForm() {
  const [v, setV] = useState(INIT);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setV({ ...v, [k]: e?.target ? e.target.value : e });

  const submit = async (ev) => {
    ev.preventDefault();
    const e = {};
    if (v.name.trim().length < 2) e.name = "Ad soyad girin.";
    if (!isPhone(v.phone)) e.phone = "Geçerli bir telefon numarası girin.";
    if (!isEmail(v.email)) e.email = "Geçerli bir e-posta adresi girin.";
    if (v.message.trim().length < 5) e.message = "Mesajınızı yazın.";
    if (!v.kvkk_ack) e.kvkk_ack = "Aydınlatma metnini okuduğunuzu onaylayın.";
    setErrors(e);
    if (Object.keys(e).length) return;
    setLoading(true);
    try {
      await api.post("/public/contact", v);
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };
  if (done) return <SuccessBox testid="contact-success" title="Mesajınız alındı" text="Teşekkür ederiz. En kısa sürede size dönüş yapacağız." />;
  const input = (k, p = {}) => <input id={`c-${k}`} value={v[k]} onChange={set(k)} className="field-input" data-testid={`contact-${k}`} {...p} />;
  return (
    <form onSubmit={submit} noValidate className="relative space-y-5 rounded-lg border border-zinc-200 bg-white p-5 sm:p-8" data-testid="contact-form">
      <h2 className="text-xl font-bold text-zinc-950">Bize yazın</h2>
      <Honeypot value={v.website} onChange={set("website")} />
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="c-name" label="Ad Soyad" required error={errors.name}>{input("name", { autoComplete: "name" })}</Field>
        <Field id="c-phone" label="Telefon" required error={errors.phone}>{input("phone", { type: "tel", inputMode: "tel" })}</Field>
        <Field id="c-email" label="E-posta" required error={errors.email}>{input("email", { type: "email" })}</Field>
        <Field id="c-subject" label="Konu">{input("subject")}</Field>
      </div>
      <Field id="c-message" label="Mesajınız" required error={errors.message}>
        <textarea id="c-message" rows={5} maxLength={3000} value={v.message} onChange={set("message")} className="field-input" data-testid="contact-message" />
      </Field>
      <ConsentBox id="contact_kvkk" checked={v.kvkk_ack} onChange={set("kvkk_ack")} error={errors.kvkk_ack} to="/kvkk-aydinlatma-metni" linkText="KVKK Aydınlatma Metni'ni" rest="okudum. *" />
      <SubmitBtn loading={loading} testid="contact-submit-btn">Mesajı Gönder</SubmitBtn>
    </form>
  );
}

export default function Contact() {
  const { settings: s } = useSite();
  const row = (Icon, label, value, href, testid, ext) => (
    <a href={href} target={ext ? "_blank" : undefined} rel="noopener noreferrer" className="flex gap-4 rounded-lg border border-zinc-200 bg-white p-5 transition-colors hover:border-[color:var(--red)]" data-testid={testid}>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-[color:var(--ink)] text-white"><Icon className="h-5 w-5" /></span>
      <span><span className="block text-xs font-bold uppercase tracking-widest text-zinc-500">{label}</span><span className="mt-1 block text-sm font-semibold text-zinc-900">{value}</span></span>
    </a>
  );
  return (
    <>
      <Seo title={s.seo?.contact?.title} description={s.seo?.contact?.description} path="/iletisim" jsonLd={s.legal_name ? businessLd(s) : null} />
      <PageHero title="İletişim" subtitle="Bilgi ve teklif için bize telefon, WhatsApp veya form üzerinden ulaşabilirsiniz." image="/img/kusadasi.webp" crumbs={[{ name: "İletişim" }]} />
      <div className="bg-zinc-50">
        <div className="container-x grid gap-10 py-12 sm:py-16 lg:grid-cols-2">
          <div className="space-y-4">
            {row(Phone, "Telefon", s.phone, telHref(s.phone), "contact-info-phone")}
            {row(MessageCircle, "WhatsApp", "Hazır mesajla hemen yazın", waHref(s), "contact-info-whatsapp", true)}
            {s.email && row(Mail, "E-posta", s.email, `mailto:${s.email}`, "contact-info-email")}
            {row(MapPin, "Adres", s.address, s.maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address || "")}`, "contact-info-address", true)}
            <MapBox query={s.maps_embed_query || s.address} />
            <div className="flex flex-col gap-3 sm:flex-row">
              {s.maps_url && (
                <a href={s.maps_url} target="_blank" rel="noopener noreferrer" className="btn-outline" data-testid="contact-maps-btn">
                  <MapPin className="h-4 w-4" /> Google Haritalar'da Görüntüle
                </a>
              )}
              <a href={s.maps_directions_url || `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(s.address || "")}`} target="_blank" rel="noopener noreferrer" className="btn-red" data-testid="contact-directions-btn">
                <Navigation className="h-4 w-4" /> Yol Tarifi Al
              </a>
            </div>
            <div className="space-y-5 rounded-lg border border-zinc-200 bg-white p-5">
              <div>
                <p className="mb-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Sosyal medya</p>
                <SocialLinks variant="light" labels testid="contact-social" />
              </div>
              <WorkingHours />
            </div>
          </div>
          <div className="space-y-8">
            <ContactForm />
            <CallbackForm />
          </div>
        </div>
      </div>
    </>
  );
}
